import {
	SpaceKind,
	TileGroupAccessKind,
	TileGroupType,
	TileGroupWidth
} from "client/Api/Workspace/workspace";
import { InteractionState } from "../../Tiles/TileContext";
import {
	groupWidthToTileGroupColumnSpan,
	PackingAlgorithmKind,
	SpaceOptions,
	TileGridConfiguration,
	TileGridRenderSettings,
	TileGroupColumnSpanInTileGroupGrid,
	TileGroupRenderState
} from "../common";
import { never } from "client_legacy/ndaw/errors";
import * as tileGridSelectors from "./tileGrid";
import { IPackingAlgorithm } from "client_legacy/Workspace/Packing/common";
import { TopLeftPackingAlgorithm } from "client_legacy/Workspace/Packing/topLeft";
import { TopPackingAlgorithm } from "client_legacy/Workspace/Packing/top";
import { LargeToSmallTopLeftFillPackingAlgorithm } from "client_legacy/Workspace/Packing/largeToSmallTopLeftFill";

export function getTileInteractionState(
	currentState: Pick<TileGroupRenderState, "data">,
	isInEditMode: boolean
): InteractionState {
	return isInEditMode
		? currentState.data
			.accessKind === TileGroupAccessKind.Edit
				? InteractionState.Selectable
				: InteractionState.Disabled
		: InteractionState.Default;
}

export function isHidden(
	currentState: Pick<TileGroupRenderState, "data" | "visibilityPreference">,
	spaceOptions: OptionalModel<SpaceOptions>
): boolean {
	const {
		data,
		visibilityPreference
	} = currentState;

	const shouldBeVisibleByDefault: boolean = spaceOptions.isSet
		&& spaceOptions.value.kind === SpaceKind.Personal
		&& !data.isHiddenByDefaultOnPersonalSpace;

	if (!visibilityPreference.isSet) {
		return !shouldBeVisibleByDefault;
	}

	switch (visibilityPreference.value) {
		case "visible":
			return false;
		case "hidden":
			return true;
		default:
			never(visibilityPreference.value);
	}
}

export function isHiddenByConditionalAccess(
	currentState: Pick<TileGroupRenderState, "data">,
	isInEditMode: boolean,
	spaceOptions: OptionalModel<SpaceOptions>
): boolean {
	const {
		data
	} = currentState;

	if (!data.conditionalAccess) {
		return false;
	}

	if (!isInEditMode) {
		return true;
	}

	if (data.accessKind === TileGroupAccessKind.Edit) {
		return false;
	}

	if (data.kind === TileGroupType.Shared
		&& spaceOptions.isSet
		&& spaceOptions.value.kind === SpaceKind.Shared
	) {
		return false;
	}

	return true;
}

export function isHiddenFromListing(
	currentState: Pick<TileGroupRenderState, "data" | "tileGrid">,
	isInEditMode: boolean,
	spaceOptions: OptionalModel<SpaceOptions>
): boolean {
	const {
		data,
		tileGrid
	} = currentState;

	if (isInEditMode
		&& data.kind === TileGroupType.Shared
		&& spaceOptions.isSet
		&& spaceOptions.value.kind === SpaceKind.Shared
	) {
		return false;
	}

	if (data.accessKind === TileGroupAccessKind.NoAccess) {
		return true;
	}

	return isHiddenByConditionalAccess(
		currentState,
		isInEditMode,
		spaceOptions
	)
		|| (
			data.accessKind !== TileGroupAccessKind.Edit
			&& tileGridSelectors.isNotEmptyAndAllTilesAreHiddenByConditionalAccess(tileGrid)
		);
}

const tileGroupWidthInAscendingOrder: readonly TileGroupWidth[] = [
	TileGroupWidth.Regular,
	TileGroupWidth.Wide
];

export function selectNewWidth(
	currentState: TileGroupRenderState,
	regularGroupWidthTileGridConfiguration: TileGridConfiguration,
	minimalTileColumnSpan: number
): TryResult<TileGroupWidth> {
	const { width } = currentState.data;

	const currentWidthIndex: number = tileGroupWidthInAscendingOrder.indexOf(width);

	if (currentWidthIndex === -1) {
		throw new Error(`Current width value ${width} was not found in the tileGroupWidthInAscendingOrder array.`);
	}

	for (let i = currentWidthIndex + 1; i < tileGroupWidthInAscendingOrder.length; i++) {
		const width: TileGroupWidth = tileGroupWidthInAscendingOrder[i];
		const colSpan: number = groupWidthToTileGroupColumnSpan[width];

		const configuration: TileGridConfiguration = calculateTileGridConfiguration(
			regularGroupWidthTileGridConfiguration,
			colSpan
		);

		if (configuration.dimensionLimits.columns >= minimalTileColumnSpan) {
			return {
				wasSuccessful: true,
				result: width
			};
		}
	}

	return {
		wasSuccessful: false
	};
}

export function createTileGridRenderSettings(
	automaticallyReorderTiles: boolean,
	regularGroupWidthTileGridConfiguration: TileGridConfiguration,
	tileGroupColumnSpan: TileGroupColumnSpanInTileGroupGrid
): TileGridRenderSettings {
	const configuredPackingAlgorithm: PackingAlgorithmKind = automaticallyReorderTiles
		? PackingAlgorithmKind.LargeToSmallTopLeftFill
		: PackingAlgorithmKind.Top;

	const tileGridConfiguration: TileGridConfiguration = calculateTileGridConfiguration(
		regularGroupWidthTileGridConfiguration,
		tileGroupColumnSpan.value
	);

	const isWidthReduced: boolean = tileGroupColumnSpan.kind === "reduced";

	let packingAlgorithm: IPackingAlgorithm;

	if (tileGridConfiguration.renderLiveTilesFullWidth || isWidthReduced) {
		packingAlgorithm = new TopLeftPackingAlgorithm(
			tileGridConfiguration.dimensionLimits
				.columns
		);
	} else {
		switch (configuredPackingAlgorithm) {
			case PackingAlgorithmKind.Top:
				packingAlgorithm = new TopPackingAlgorithm();
				break;
			case PackingAlgorithmKind.LargeToSmallTopLeftFill:
				packingAlgorithm = new LargeToSmallTopLeftFillPackingAlgorithm(
					tileGridConfiguration.dimensionLimits
						.columns
				);
				break;
			default:
				never(configuredPackingAlgorithm);
		}
	}

	return {
		configuredPackingAlgorithm,
		configuration: tileGridConfiguration,
		isWidthReduced,
		packingAlgorithm
	};
}

function calculateTileGridConfiguration(
	regularGroupWidthTileGridConfiguration: TileGridConfiguration,
	tileGroupColumnSpan: number
): TileGridConfiguration {
	let tileGridConfiguration: TileGridConfiguration = regularGroupWidthTileGridConfiguration;

	if (tileGroupColumnSpan > 1) {
		tileGridConfiguration = {
			renderLiveTilesFullWidth: tileGridConfiguration.renderLiveTilesFullWidth,
			dimensionLimits: {
				...tileGridConfiguration.dimensionLimits,
				columns: ((tileGridConfiguration.dimensionLimits.columns + 1) * tileGroupColumnSpan) - 1
			}
		};
	}

	return tileGridConfiguration;
}